/**
 * Phase 7-B — Listing detail page (`/marketplace/listings/:id`).
 *
 * Surfaces `GET /marketplace/listings/{id}` (`02_api_spec.md §3.3`) — full
 * listing body + last 5 attestations + recent badges. Header has the Hire CTA
 * which mounts the HireModal.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import Badge from '../../components/Badge';
import HireModal from './HireModal';
import {
  weiToAdi,
  listingStatusBadge,
  shortAddr,
} from './utils';
import type {
  Attestation,
  BadgesPage,
  MarketplaceListing,
  ReputationBadge,
} from '@infinia/ida-sdk';
import { useAuth } from '../../auth/AuthContext';
import { encodePathSegment } from '../../lib/url';

export default function ListingDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['marketplace', 'common']);
  const { wallet } = useAuth();

  const [listing, setListing] = useState<MarketplaceListing | null>(null);
  const [badges, setBadges] = useState<ReputationBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hireOpen, setHireOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const l = await apiFetch<MarketplaceListing>(`/marketplace/listings/${encodePathSegment(id)}`);
        if (cancelled) return;
        setListing(l);
        // Fire-and-forget badges; the listing body already gives us the count.
        try {
          const b = await apiFetch<BadgesPage>(
            `/marketplace/badges?agentDid=${encodeURIComponent(l.agentDid)}&limit=5&sort=minted_at_desc`,
          );
          if (!cancelled) setBadges(b.items ?? []);
        } catch {
          if (!cancelled) setBadges([]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'load_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [id]);

  // Buyer DID for hire flow — derived from auth context wallet, if any.
  const buyerDid = (wallet as { did?: string } | undefined)?.did;

  if (loading) {
    return <div className="text-center py-20 text-sm text-text-faint">{t('detail.loading')}</div>;
  }
  if (error || !listing) {
    return (
      <div className="text-center py-20 text-sm text-red-500">
        {error || t('detail.notFound')}
        <div className="mt-2">
          <button onClick={() => navigate('/agent-marketplace')} className="text-primary text-xs underline">
            {t('detail.back')}
          </button>
        </div>
      </div>
    );
  }

  const trust = listing.trustSnapshot?.trustScore ?? 0;
  const badgeCount = listing.trustSnapshot?.badgeCount ?? 0;
  const recent = listing.recentAttestations ?? [];

  const canHire = listing.status === 'active';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/agent-marketplace" className="hover:text-primary">{t('title')}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main truncate max-w-md">{listing.title}</span>
      </nav>

      {/* Header */}
      <div className="rounded-xl border border-border-subtle bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-text-main truncate">{listing.title}</h1>
              <Badge variant={listingStatusBadge(listing.status)}>
                {t(`browse.statusLabels.${listing.status}`)}
              </Badge>
            </div>
            <p className="text-xs text-text-faint truncate">{listing.capabilityScope}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-text-muted">{t('hire.review.price')}</p>
              <p className="text-lg font-black text-text-main">
                {weiToAdi(listing.pricing.priceWei)} <span className="text-xs font-bold text-text-muted">{listing.pricing.currency}</span>
              </p>
            </div>
            <button
              onClick={() => setHireOpen(true)}
              disabled={!canHire}
              className="btn-primary disabled:opacity-50"
            >
              {canHire ? t('detail.hire') : t('detail.hireDisabled')}
            </button>
          </div>
        </div>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left + middle (description, capabilities, MCP refs, IBCT, schema, SLA) */}
        <div className="lg:col-span-2 space-y-5">
          {listing.description && (
            <Section title={t('detail.capability')}>
              <p className="whitespace-pre-line text-sm text-text-main">{listing.description}</p>
            </Section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KvCard label={t('detail.operator')} value={shortAddr(listing.operatorDid, 8)} mono />
            <KvCard label={t('detail.agent')} value={shortAddr(listing.agentDid, 8)} mono />
            <KvCard label={t('detail.capability')} value={listing.capabilityScope} mono />
            {listing.mcpToolId && <KvCard label={t('detail.mcpTool')} value={listing.mcpToolId} mono />}
            {listing.ibctPolicyId && <KvCard label={t('detail.policy')} value={listing.ibctPolicyId} mono />}
          </div>

          {listing.schemaPins && listing.schemaPins.length > 0 && (
            <Section title={t('detail.schemaPins')}>
              <div className="flex flex-wrap gap-1.5">
                {listing.schemaPins.map((p) => (
                  <span key={p} className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold text-slate-600">
                    {p}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {listing.sla && (listing.sla.latencyP95Ms || listing.sla.uptimePct) && (
            <Section title={t('detail.sla')}>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {listing.sla.latencyP95Ms !== undefined && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-text-faint">{t('detail.slaLatency')}</p>
                    <p className="font-bold text-text-main">{listing.sla.latencyP95Ms} ms</p>
                  </div>
                )}
                {listing.sla.uptimePct !== undefined && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-text-faint">{t('detail.slaUptime')}</p>
                    <p className="font-bold text-text-main">{listing.sla.uptimePct}%</p>
                  </div>
                )}
              </div>
            </Section>
          )}

          {listing.sampleInputOutput && (
            <Section title={t('detail.sample')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="mb-1 text-[10px] uppercase tracking-wide text-text-faint">{t('detail.sampleInput')}</p>
                  <pre className="overflow-x-auto rounded-lg bg-slate-50 p-2 font-mono text-[11px] text-text-main">
                    {JSON.stringify(listing.sampleInputOutput.input ?? {}, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="mb-1 text-[10px] uppercase tracking-wide text-text-faint">{t('detail.sampleOutput')}</p>
                  <pre className="overflow-x-auto rounded-lg bg-slate-50 p-2 font-mono text-[11px] text-text-main">
                    {JSON.stringify(listing.sampleInputOutput.output ?? {}, null, 2)}
                  </pre>
                </div>
              </div>
            </Section>
          )}
        </div>

        {/* Right rail (trust, badges, attestations) */}
        <aside className="space-y-5">
          <Section title={t('detail.trust')}>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-text-main">{trust}</span>
              <span className="text-xs text-text-faint">{t('detail.trustOf')}</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${trust >= 80 ? 'bg-emerald-500' : trust >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${Math.max(0, Math.min(100, trust))}%` }}
              />
            </div>
          </Section>

          <Section title={t('detail.badgesHeading')}>
            {badges.length === 0 ? (
              <p className="text-xs text-text-faint">{t('detail.badgesEmpty')}</p>
            ) : (
              <ul className="space-y-2">
                {badges.map((b) => (
                  <li key={b.tokenId} className="flex items-center gap-2 rounded-lg border border-border-subtle p-2">
                    <span className="material-symbols-outlined text-purple-600" style={{ fontSize: 18 }}>workspace_premium</span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-xs font-bold text-text-main">{b.capabilityScope}</p>
                      <p className="text-[10px] text-text-faint">{new Date(b.mintedAt).toLocaleDateString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[10px] text-text-faint">Total: {badgeCount}</p>
          </Section>

          <Section title={t('detail.attestationsHeading')}>
            {recent.length === 0 ? (
              <p className="text-xs text-text-faint">{t('detail.attestationsEmpty')}</p>
            ) : (
              <ul className="space-y-2">
                {recent.map((a: Attestation) => (
                  <li key={a.id} className="rounded-lg border border-border-subtle p-2">
                    <div className="flex items-center justify-between">
                      <Badge variant={a.scoreBucket === 'positive' ? 'success' : a.scoreBucket === 'neutral' ? 'warning' : 'error'}>
                        {t('detail.attestationScore', { score: a.score })}
                      </Badge>
                      <span className="text-[10px] text-text-faint">{new Date(a.createdAt).toLocaleDateString()}</span>
                    </div>
                    {a.rationale && <p className="mt-1 line-clamp-2 text-xs text-text-muted">{a.rationale}</p>}
                    <p className="mt-1 truncate font-mono text-[10px] text-text-faint">
                      {t('detail.attestationVerifier', { did: shortAddr(a.verifierDid, 6) })}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </aside>
      </div>

      <HireModal open={hireOpen} onClose={() => setHireOpen(false)} listing={listing} buyerDid={buyerDid} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-4">
      <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{title}</h3>
      {children}
    </div>
  );
}

function KvCard({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-white p-3">
      <p className="text-[10px] uppercase tracking-wide text-text-faint">{label}</p>
      <p className={`mt-0.5 truncate text-xs ${mono ? 'font-mono' : ''} text-text-main`}>{value}</p>
    </div>
  );
}
